import React, { useState, useRef, useEffect } from 'react';
import { View, Text, TouchableOpacity, TextInput, StyleSheet, Modal, Platform } from 'react-native';
import {
  FencingCardIcon,
  SwapIcon,
  TimerIcon,
  RefreshIcon
} from './FencingIcons';

const PlatformOverlay = ({ color, onClose }) => {
  // Verifica se siamo in un ambiente browser prima di accedere a navigator
  const isBrowser = typeof window !== 'undefined' && typeof navigator !== 'undefined';
  const isMobile = isBrowser && /Mobi|Android|iPhone/i.test(navigator.userAgent);

  // Stile overlay: usa sempre position absolute/fixed per garantire copertura totale
  const overlayStyle = {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: color,
    zIndex: 9999,
    width: '100%',
    height: '100%',
  };

  // Su mobile usa Modal per garantire rendering sopra tutto
  if (isMobile) {
    return (
      <Modal visible transparent={true} animationType="fade" onRequestClose={onClose}>
        <TouchableOpacity style={{ flex: 1, backgroundColor: color }} activeOpacity={1} onPress={onClose}>
          <View style={{ flex: 1, backgroundColor: color }} />
        </TouchableOpacity>
      </Modal>
    );
  } else {
    return (
      <div style={{ ...overlayStyle, position: 'fixed' }} onClick={onClose} />
    );
  }
};

const Scorekeeper = () => {
  // Funzione per caricare dati salvati
  const loadData = () => {
    // Verifica se siamo in un ambiente browser prima di accedere a localStorage
    if (typeof window === 'undefined') {
      return {
        scoreL: 0,
        scoreR: 0,
        nameL: 'Tiratore A',
        nameR: 'Tiratore B',
        yellowCountL: 0,
        yellowCountR: 0,
        redCountL: 0,
        redCountR: 0
      };
    }

    try {
      const saved = localStorage.getItem('scorekeeperData');
      if (!saved) return null;
      const data = JSON.parse(saved);
      return {
        scoreL: Number(data.scoreL) || 0,
        scoreR: Number(data.scoreR) || 0,
        nameL: data.nameL || 'Tiratore A',
        nameR: data.nameR || 'Tiratore B',
        yellowCountL: Number(data.yellowCountL) || 0,
        yellowCountR: Number(data.yellowCountR) || 0,
        redCountL: Number(data.redCountL) || 0,
        redCountR: Number(data.redCountR) || 0
      };
    } catch {
      return null;
    }
  };

  const savedData = loadData();

  // Score e nomi
  const [scoreL, setScoreL] = useState(savedData?.scoreL ?? 0); // Rosso (sinistra)
  const [scoreR, setScoreR] = useState(savedData?.scoreR ?? 0); // Verde (destra)
  const [nameL, setNameL] = useState(savedData?.nameL ?? 'Tiratore A');
  const [nameR, setNameR] = useState(savedData?.nameR ?? 'Tiratore B');
  // Cartellini (stato attivo temporaneo)
  const [yellowL, setYellowL] = useState(false);
  const [redL, setRedL] = useState(false);
  const [yellowR, setYellowR] = useState(false);
  const [redR, setRedR] = useState(false);
  // Conteggio cartellini
  const [yellowCountL, setYellowCountL] = useState(savedData?.yellowCountL ?? 0);
  const [redCountL, setRedCountL] = useState(savedData?.redCountL ?? 0);
  const [yellowCountR, setYellowCountR] = useState(savedData?.yellowCountR ?? 0);
  const [redCountR, setRedCountR] = useState(savedData?.redCountR ?? 0);
  // Overlay
  const [overlay, setOverlay] = useState(null); // {color, label} oppure null
  // Timer
  const [timer, setTimer] = useState(180); // default 3'
  const [running, setRunning] = useState(false);
  const timerRef = useRef();

  // Refs per gestire il focus
  const nameLRef = useRef();
  const nameRRef = useRef();

  // Funzione per salvare i dati
  const saveData = (data) => {
    try {
      // Verifica se siamo in un ambiente browser prima di accedere a localStorage
      if (typeof window !== 'undefined') {
        localStorage.setItem('scorekeeperData', JSON.stringify(data));
      }
    } catch (error) {
      console.error('Errore nel salvataggio dei dati:', error);
    }
  };

  // Salva automaticamente
  useEffect(() => {
    const data = {
      scoreL, scoreR, nameL, nameR,
      yellowCountL, yellowCountR, redCountL, redCountR
    };
    saveData(data);
  }, [scoreL, scoreR, nameL, nameR, yellowCountL, yellowCountR, redCountL, redCountR]);

  // Timer logic
  useEffect(() => {
    if (running && timer > 0) {
      timerRef.current = setTimeout(() => setTimer(t => t - 1), 1000);
    } else {
      clearTimeout(timerRef.current);
    }
    return () => clearTimeout(timerRef.current);
  }, [running, timer]);

  const resetAll = () => {
    setScoreL(0); setScoreR(0);
    setNameL(''); setNameR(''); // Resetta a stringa vuota
    setYellowL(false); setRedL(false);
    setYellowR(false); setRedR(false);
    setYellowCountL(0); setYellowCountR(0);
    setRedCountL(0); setRedCountR(0);
    setTimer(180); setRunning(false);
    localStorage.removeItem('scorekeeperData');
    if (nameLRef.current) nameLRef.current.focus();
    if (nameRRef.current) nameRRef.current.focus();
  };

  const resetCards = () => {
    setYellowL(false); setRedL(false);
    setYellowR(false); setRedR(false);
    setYellowCountL(0); setYellowCountR(0);
    setRedCountL(0); setRedCountR(0);
  };

  const swapSides = () => {
    setScoreL(scoreR); setScoreR(scoreL);
    setNameL(nameR); setNameR(nameL);
    setYellowL(yellowR); setYellowR(yellowL);
    setRedL(redR); setRedR(redL);
  };
  // Cartellini: overlay a tutto schermo
  const showOverlay = (color) => setOverlay({ color });
  const closeOverlay = () => setOverlay(null);
  // Gestione cartellini: logica FIE
  // - 1° tocco giallo: assegna cartellino giallo (solo avvertimento)
  // - Dal 2° tocco giallo in poi: diventa automaticamente rosso (punto all'avversario)
  const toggleYellow = side => {
    if (side === 'L') {
      if (yellowCountL === 0) {
        // Primo giallo: solo avvertimento
        setYellowL(true);
        setYellowCountL(1);
        showOverlay('#ffe082');
        setTimeout(() => setYellowL(false), 100);
      } else {
        // Già un giallo: questo diventa rosso (punto all'avversario)
        setRedL(true);
        setRedCountL(c => c + 1);
        setScoreR(s => s + 1); // Punto all'avversario
        showOverlay('#d32f2f');
        setTimeout(() => setRedL(false), 100);
      }
    } else {
      if (yellowCountR === 0) {
        // Primo giallo: solo avvertimento
        setYellowR(true);
        setYellowCountR(1);
        showOverlay('#ffe082');
        setTimeout(() => setYellowR(false), 100);
      } else {
        // Già un giallo: questo diventa rosso (punto all'avversario)
        setRedR(true);
        setRedCountR(c => c + 1);
        setScoreL(s => s + 1); // Punto all'avversario
        showOverlay('#d32f2f');
        setTimeout(() => setRedR(false), 100);
      }
    }
  };

  const toggleRed = side => {
    if (side === 'L') {
      setRedL(true);
      setRedCountL(c => c + 1);
      setScoreR(s => s + 1);
      showOverlay('#d32f2f');
      setTimeout(() => setRedL(false), 100);
    } else {
      setRedR(true);
      setRedCountR(c => c + 1);
      setScoreL(s => s + 1);
      showOverlay('#d32f2f');
      setTimeout(() => setRedR(false), 100);
    }
  };


  // Score
  const inc = side => side === 'L' ? setScoreL(s => s + 1) : setScoreR(s => s + 1);
  const dec = side => side === 'L' ? setScoreL(s => s > 0 ? s - 1 : 0) : setScoreR(s => s > 0 ? s - 1 : 0);
  // Timer
  const handleTimeTap = () => setRunning(r => !r);
  const setPreset = sec => { setTimer(sec); setRunning(false); };
  const resetTimer = () => { setTimer(180); setRunning(false); };
  // Format mm:ss
  const fmt = t => `${Math.floor(t / 60).toString().padStart(2, '0')}:${(t % 60).toString().padStart(2, '0')}`;

  return (
    <View style={styles.container}>
      {/* Timer */}
      <View style={styles.timerContainer}>
        <TouchableOpacity style={styles.timerBox} onPress={handleTimeTap}>
          <Text style={[styles.timerText, running && { color: '#1976d2' }]}>{fmt(timer)}</Text>
          <Text style={styles.timerLabel}>{running ? 'PAUSA' : 'START'}</Text>
        </TouchableOpacity>

        <View style={styles.timerPresetRow}>
          <TouchableOpacity style={styles.presetBtn} onPress={() => setPreset(180)}>
            <Text style={styles.presetText}>3:00</Text>
          </TouchableOpacity>
          <TouchableOpacity style={styles.presetBtn} onPress={() => setPreset(60)}>
            <Text style={styles.presetText}>1:00</Text>
          </TouchableOpacity>
          <TouchableOpacity style={styles.presetBtn} onPress={() => setPreset(30)}>
            <Text style={styles.presetText}>0:30</Text>
          </TouchableOpacity>
        </View>
      </View>

      {/* Nome tiratori */}
      <View style={styles.nameRow}>
        {/* Nome tiratore A (sinistra) */}
        <View style={styles.nameContainer}>
          <TextInput
            ref={nameLRef}
            style={[styles.nameInput, { borderColor: '#d32f2f', backgroundColor: '#fff5f5' }]}
            value={nameL}
            onChangeText={setNameL}
            maxLength={18}
            onFocus={() => nameL === 'Tiratore A' && setNameL('')}
            placeholder="Nome Tiratore A"
          />
        </View>

        <View style={{ width: 40 }} />

        {/* Nome tiratore B (destra) */}
        <View style={styles.nameContainer}>
          <TextInput
            ref={nameRRef}
            style={[styles.nameInput, { borderColor: '#43a047', backgroundColor: '#f5fff7' }]}
            value={nameR}
            onChangeText={setNameR}
            maxLength={18}
            onFocus={() => nameR === 'Tiratore B' && setNameR('')}
            placeholder="Nome Tiratore B"
          />
        </View>
      </View>

      {/* Score row */}
      <View style={styles.row}>
        {/* Rosso (sinistra) */}
        <TouchableOpacity style={[styles.scoreBox, { backgroundColor: '#ffeaea', borderColor: '#d32f2f' }]} onPress={() => inc('L')}>
          <View style={styles.penaltyRow}>
            <TouchableOpacity style={[styles.card, yellowL && styles.cardActiveYellow]} onPress={() => toggleYellow('L')}>
              <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                <FencingCardIcon style={{ fontSize: 22, color: '#fbc02d' }} />
                {yellowCountL > 0 ? <Text style={styles.cardNum}>{yellowCountL}</Text> : null}
              </View>
            </TouchableOpacity>
            <TouchableOpacity style={[styles.card, redL && styles.cardActiveRed]} onPress={() => toggleRed('L')}>
              <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                <FencingCardIcon style={{ fontSize: 22, color: '#d32f2f' }} />
                {redCountL > 0 ? <Text style={styles.cardNum}>{redCountL}</Text> : null}
              </View>
            </TouchableOpacity>
          </View>
          <Text style={[styles.score, { color: '#d32f2f' }]}>{scoreL}</Text>
          <View style={styles.btnRow}>
            <TouchableOpacity style={[styles.btn, { padding: 8 }]} onPress={() => dec('L')}>
              <Text style={[styles.btnText, { fontSize: 18 }]}>-</Text>
            </TouchableOpacity>
          </View>
        </TouchableOpacity>

        {/* Swap btn */}
        <View style={styles.swapCol}>
          <TouchableOpacity style={styles.swapBtn} onPress={swapSides}>
            <SwapIcon />
          </TouchableOpacity>
        </View>

        {/* Verde (destra) */}
        <TouchableOpacity style={[styles.scoreBox, { backgroundColor: '#e9ffe9', borderColor: '#388e3c' }]} onPress={() => inc('R')}>
          <View style={styles.penaltyRow}>
            <TouchableOpacity style={[styles.card, yellowR && styles.cardActiveYellow]} onPress={() => toggleYellow('R')}>
              <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                <FencingCardIcon style={{ fontSize: 22, color: '#fbc02d' }} />
                {yellowCountR > 0 ? <Text style={styles.cardNum}>{yellowCountR}</Text> : null}
              </View>
            </TouchableOpacity>
            <TouchableOpacity style={[styles.card, redR && styles.cardActiveRed]} onPress={() => toggleRed('R')}>
              <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                <FencingCardIcon style={{ fontSize: 22, color: '#d32f2f' }} />
                {redCountR > 0 ? <Text style={styles.cardNum}>{redCountR}</Text> : null}
              </View>
            </TouchableOpacity>
          </View>
          <Text style={[styles.score, { color: '#388e3c' }]}>{scoreR}</Text>
          <View style={styles.btnRow}>
            <TouchableOpacity style={[styles.btn, { padding: 8 }]} onPress={() => dec('R')}>
              <Text style={[styles.btnText, { fontSize: 18 }]}>-</Text>
            </TouchableOpacity>
          </View>
        </TouchableOpacity>
      </View>
      {/* Pulsanti Reset */}
      <View style={styles.resetButtonsContainer}>
        <TouchableOpacity style={styles.resetButton} onPress={resetCards}>
          <View style={{ flexDirection: 'row', alignItems: 'center' }}>
            <FencingCardIcon style={{ fontSize: 16, color: '#fbc02d', marginRight: 2 }} />
            <FencingCardIcon style={{ fontSize: 16, color: '#d32f2f', marginRight: 5 }} />
            <Text style={styles.resetButtonText}>Card Reset</Text>
          </View>
        </TouchableOpacity>

        <TouchableOpacity style={styles.resetButton} onPress={resetTimer}>
          <View style={{ flexDirection: 'row', alignItems: 'center' }}>
            <TimerIcon style={{ fontSize: 18, color: '#1976d2', marginRight: 5 }} />
            <Text style={styles.resetButtonText}>Time Reset</Text>
          </View>
        </TouchableOpacity>

        <TouchableOpacity style={[styles.resetButton, { backgroundColor: '#ffebee' }]} onPress={resetAll}>
          <View style={{ flexDirection: 'row', alignItems: 'center' }}>
            <RefreshIcon style={{ fontSize: 18, color: '#d32f2f', marginRight: 5 }} />
            <Text style={[styles.resetButtonText, { color: '#d32f2f' }]}>Total Reset</Text>
          </View>
        </TouchableOpacity>
      </View>
      {/* Istruzioni */}
      <Text style={styles.instructionText}>Tocca la card per assegnare il punto!</Text>
      {/* Overlay cartellino */}
      {overlay && <PlatformOverlay color={overlay.color} onClose={closeOverlay} />}
    </View>
  );
};

const styles = StyleSheet.create({
  container: { backgroundColor: '#fff', borderRadius: 14, padding: 18, alignItems: 'center', marginBottom: 24, shadowColor: '#000', shadowOpacity: 0.07, shadowRadius: 4, elevation: 2, width: '100%', maxWidth: 520 },
  timerContainer: {
    alignItems: 'center',
    marginBottom: 24,
    gap: 16
  },
  timerPresetRow: {
    flexDirection: 'row',
    justifyContent: 'center',
    gap: 12,
    marginTop: 0
  },
  timerBox: {
    backgroundColor: '#f7fafd',
    borderRadius: 12,
    padding: 18,
    alignItems: 'center',
    minWidth: 120,
    borderWidth: 2,
    borderColor: '#1976d2'
  },
  timerText: { fontSize: 32, fontWeight: 'bold', color: '#333', letterSpacing: 2 },
  timerLabel: { fontSize: 12, color: '#888', marginTop: 4 },
  presetBtn: { backgroundColor: '#e3f2fd', borderRadius: 8, paddingVertical: 6, paddingHorizontal: 16, marginHorizontal: 3 },
  presetText: { color: '#1976d2', fontWeight: 'bold', fontSize: 15 },
  nameRow: { flexDirection: 'row', justifyContent: 'space-between', width: '100%', marginBottom: 12 },
  nameContainer: { flex: 1, maxWidth: '48%' },
  nameInput: {
    fontSize: 16,
    fontWeight: 'bold',
    padding: 10,
    borderWidth: 2,
    borderRadius: 8,
    textAlign: 'center',
    width: '100%'
  },
  row: { flexDirection: 'row', justifyContent: 'center', width: '100%', marginBottom: 8 },
  scoreBox: { flex: 1, alignItems: 'center', marginHorizontal: 6, borderWidth: 2, borderRadius: 12, padding: 14 },
  penaltyRow: { flexDirection: 'row', marginBottom: 6, gap: 8 },
  card: { width: 32, height: 32, borderRadius: 6, borderWidth: 2, borderColor: '#bbb', alignItems: 'center', justifyContent: 'center', backgroundColor: '#fff', marginHorizontal: 2 },
  cardActiveYellow: { backgroundColor: '#fffbe7', borderColor: '#fbc02d' },
  cardActiveRed: { backgroundColor: '#ffeaea', borderColor: '#d32f2f' },
  cardText: { fontSize: 22, flexDirection: 'row', alignItems: 'center', justifyContent: 'center' },
  cardNum: { fontSize: 13, fontWeight: 'bold', color: '#333', marginLeft: -4, backgroundColor: 'transparent' },
  score: { fontSize: 40, fontWeight: 'bold', marginBottom: 8 },
  btnRow: { flexDirection: 'row', marginTop: 2 },
  btn: { backgroundColor: '#e3f2fd', borderRadius: 8, paddingVertical: 8, paddingHorizontal: 20, marginHorizontal: 5 },
  btnText: { fontSize: 22, color: '#1976d2', fontWeight: 'bold' },
  swapCol: { justifyContent: 'center', alignItems: 'center', width: 44 },
  swapBtn: { backgroundColor: '#e3f2fd', borderRadius: 8, padding: 8, marginVertical: 12, borderWidth: 2, borderColor: '#1976d2' },
  swapIcon: { fontSize: 26, color: '#1976d2', fontWeight: 'bold' },
  resetButtonsContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    flexWrap: 'wrap',
    gap: 8,
    marginTop: 15,
    marginBottom: 10
  },
  resetButton: {
    backgroundColor: '#e3f2fd',
    borderRadius: 8,
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderWidth: 1,
    borderColor: '#1976d2',
    minWidth: 80,
    flex: 1,
    margin: 2,
    alignItems: 'center',
    justifyContent: 'center',
    maxWidth: Platform.OS === 'web' ? '30%' : undefined
  },
  resetButtonText: {
    color: '#1976d2',
    fontWeight: 'bold',
    fontSize: 13,
    textAlign: 'center'
  },
  instructionText: {
    fontSize: 14,
    color: '#666',
    marginTop: 12,
    fontStyle: 'italic',
    textAlign: 'center'
  },
  separator: {
    height: 2,
    backgroundColor: '#1976d2',
    width: '100%',
    marginBottom: 16,
    opacity: 0.2
  }
});

export default Scorekeeper;
